<script>
import Layout from '@layouts/adminLayout.vue'
import MottoIcon from '@components/motto-icon.vue'
import LoaderIcon from '@components/loader-icon.vue'
import CategorySelector from '@components/category-selector.vue'
import InfoBlock from '@components/info-block.vue'
import ResourceCarousel from '@components/resources/resources-carousel.vue'
import ResourceBlock from '@components/resources/resource-block.vue'
import ModalResource from '@components/resources/modal-resource.vue'
import { getAllResources } from '@src/api/resources'
import { resourcesComputed } from '@state/helpers'
import { transformMany as transformResources } from '@src/api/transformers/resourceTransformer'
import _ from 'lodash'

export default {
  page: {
    title: 'Resources',
    meta: [
      {
        name: 'description',
        content: 'The Resources page.',
      },
    ],
  },
  components: {
    Layout,
    MottoIcon,
    LoaderIcon,
    CategorySelector,
    InfoBlock,
    ResourceCarousel,
    ResourceBlock,
    ModalResource,
  },
  data() {
    return {
      loading: false,
      error: false,
      infoBlockMessage: 'Filter your results by selecting a category, or search with keywords.',
      selectedCategory: '',
      gridView: false,
      gridViewLabel: 'about the resources',
      gridViewEmptySentence: '',
      gridViewSelectedCategory: '',
      gridViewCategoryName: '',
      carouselView: true,
      loadedCategory: this.$route.params.category,
      loadedSubcategory: this.$route.params.subcategory,
    }
  },
  computed: {
    ...resourcesComputed,

    getAllResources() {
      if (_.isEmpty(this.allResources)) {
        return []
      }
      return this.allResources
    },

    getFavoriteResources() {
      if (_.isEmpty(this.allResources)) {
        return []
      }
      return _.orderBy(this.favoriteResources, ['favoritedAt'])
    },

    getPopularResources() {
      if (_.isEmpty(this.allResources)) {
        return []
      }
      return _.orderBy(this.allResources, ['views', 'downloadCount'], ['desc', 'desc']).slice(0, 12)
    },

    getRecentlyAddedResources() {
      if (_.isEmpty(this.allResources)) {
        return []
      }
      return this.allResources.slice(0, 12)
    },

    getDistinctCategories() {
      return _.chain(this.allResources)
        .map('categoryName')
        .uniq()
        .filter((x) => x !== null && x !== '')
        .sort()
        .value()
    },

    getDistinctSubcategories() {
      return _.chain(this.getSelectedResources)
        .map('subcategoryName')
        .uniq()
        .filter((x) => x !== null && x !== '')
        .sort()
        .value()
    },

    getSelectedResources() {
      if (_.isEmpty(this.selectedCategory)) {
        return []
      }
      return this.getAllResources.filter(
        (resource) => resource.categoryName.toLowerCase() === this.selectedCategory.toLowerCase()
      )
    },

    getSelectedCategories() {
      if (_.isEmpty(this.selectedCategory)) {
        return this.getDistinctCategories
      }
      return this.selectedCategory
    },

    gridViewResources() {
      if (this.gridViewSelectedCategory === 'favorites') {
        return this.getFavoriteResources
      }

      if (this.gridViewSelectedCategory === 'popular') {
        return this.getPopularResources
      }

      if (this.gridViewSelectedCategory === 'recent') {
        return this.getRecentlyAddedResources
      }

      if (this.gridViewSelectedCategory) {
        const resources = this.getAllResources.filter(
          (resource) => this.gridViewSelectedCategory.toLowerCase() === resource.subcategoryName.toLowerCase()
        )

        return this.sortResources(resources)
      }

      if (this.gridViewLabel === 'about the resources') {
        return this.getAllResources.filter((resource) => this.getSelectedCategories.includes(resource.category))
      }

      return this.allResources
    },
  },
  watch: {
    selectedCategory: {
      handler: function () {
        this.updateSelectedResourceCategory()
      },
    },
  },
  mounted() {
    this.fetchResources()
    this.loadParams()
    this.updateSelectedResourceCategory()
  },
  methods: {
    async fetchResources() {
      if (_.isEmpty(this.allResources) || this.shouldReloadResources) {
        this.loading = true
        try {
          const [
            {
              data: { data: resources },
            },
          ] = await Promise.all([getAllResources()])
          this.$store.commit('resources/SET_RESOURCES', transformResources(resources))
          this.$store.commit('resources/SET_SHOULD_RELOAD_RESOURCES', false)
          this.loadParams()
        } catch (err) {
          this.error = true
        } finally {
          this.loading = false
        }
      }
    },

    loadParams() {
      if (_.isEmpty(this.allResources)) {
        return
      }
      if (!this.loadedCategory) {
        return
      }
      if (['favorites', 'popular', 'recent'].includes(this.loadedCategory)) {
        this.activateGridView({
          category: this.loadedCategory,
        })
        return
      }
      if (this.loadedCategory.toLowerCase() === 'category' && this.loadedSubcategory) {
        const categoryName = this.getCategoryName(this.loadedSubcategory)
        this.$router.push({
          params: {
            category: categoryName ? categoryName.toLowerCase() : null,
            subcategory: null,
          },
        })
        return
      }
      if (!this.getDistinctCategories.map((x) => x.toLowerCase()).includes(this.loadedCategory)) {
        this.$router.push({
          params: {
            category: null,
            subcategory: null,
          },
        })
        return
      }
      this.selectedCategory = this.loadedCategory
      if (!this.loadedSubcategory) {
        return
      }
      this.activateGridView({
        category: this.loadedSubcategory,
      })
      this.gridViewCategoryName = this.loadedCategory
    },

    sortResources(resources) {
      const sortMethod = _.get(_.head(resources), 'sortMethod')
      if (!sortMethod) {
        return resources
      }

      if (sortMethod === 'alphabetical') {
        return _.orderBy(resources, 'title')
      }
    },

    updateSelectedResourceCategory() {
      this.$store.commit('resources/SET_SELECTED_RESOURCE_CATEGORY', this.selectedCategory)
    },

    getCategoryName(categoryId) {
      return _.chain(this.getAllResources.filter((resource) => resource.categoryId === parseInt(categoryId)))
        .map('categoryName')
        .uniq()
        .filter((x) => x !== null && x !== '')
        .value()[0]
    },

    changeSelectedSubcategory(subcategory) {
      if (['favorites', 'popular', 'recent'].includes(subcategory)) {
        this.$router.push({
          params: {
            category: subcategory.toLowerCase(),
            subcategory: null,
          },
        })
        return
      }
      this.$router.push({
        params: {
          subcategory: subcategory.toLowerCase(),
        },
      })
    },

    getResourcesFromSubcategory(subcategory) {
      return this.getAllResources.filter(
        (resource) =>
          resource.subcategoryName.toLowerCase() === subcategory.toLowerCase() &&
          resource.categoryName.toLowerCase() === this.selectedCategory.toLowerCase()
      )
    },

    activateGridView({ category }) {
      this.gridView = true
      this.carouselView = false
      this.gridViewLabel = null
      this.gridViewEmptySentence = null
      if (category === 'favorites') {
        this.gridViewLabel = 'Favorite Resources'
        this.gridViewEmptySentence = 'Click the heart to favorite a resource.'
      }
      if (category === 'popular') {
        this.gridViewLabel = 'Popular Items'
      }
      if (category === 'recent') {
        this.gridViewLabel = 'Recently Added'
      }
      this.gridViewSelectedCategory = category
      this.gridViewCategoryName = null
      if (!['favorites', 'popular', 'recent'].includes(this.gridViewSelectedCategory)) {
        if (this.getAllResources) {
          const randomResource = this.getAllResources.filter(
            (resource) => this.gridViewSelectedCategory.toLowerCase() === resource.subcategoryName.toLowerCase()
          )[0]
          if (randomResource) {
            this.gridViewCategoryName = randomResource.categoryName
            this.gridViewLabel = randomResource.subcategoryName
          }
        }
      }
    },

    activateCarouselView() {
      if (['favorites', 'popular', 'recent'].includes(this.loadedCategory)) {
        this.activateAllResourcesView()
        return
      }
      this.$router.push({
        params: {
          subcategory: null,
        },
      })
    },

    activateAllResourcesView() {
      this.$router.push({
        params: {
          category: null,
          subcategory: null,
        },
      })
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.viewContainer">
      <LoaderIcon v-if="loading || error" :error="error" />
      <div v-else>
        <div v-if="!gridView">
          <div class="justify-content-between">
            <BaseTitle class="mb-4" :class="$style.title">Resources</BaseTitle>
            <div :class="$style.headerContent">
              <InfoBlock :class="$style.infoBlock">
                {{ infoBlockMessage }}
              </InfoBlock>
              <CategorySelector
                :class="$style.categorySelector"
                :categories="getDistinctCategories"
                :allow-empty="true"
                :show-all-button="true"
                :change-url="true"
                :selected-category.sync="selectedCategory" />
            </div>
          </div>

          <div v-if="!selectedCategory" :class="{ [$style.gridView]: true, [$style.resourceCarousel]: true }">
            <h3>Favorite Resources</h3>
            <transition-group>
              <ResourceBlock
                v-for="resource in getFavoriteResources"
                :key="resource.resourceId"
                :class="$style.resourceBlock"
                :resource="resource" />
            </transition-group>
            <p v-if="!getFavoriteResources.length" :class="$style.emptySentence">
              Click the heart to favorite a resource.
            </p>
          </div>

          <ResourceCarousel
            v-if="!selectedCategory"
            :class="$style.resourceCarousel"
            :resources="getPopularResources"
            category="Popular Items"
            @openGridView="changeSelectedSubcategory('popular')" />
          <ResourceCarousel
            v-if="!selectedCategory"
            :class="$style.resourceCarousel"
            :resources="getRecentlyAddedResources"
            category="Recently Added"
            @openGridView="changeSelectedSubcategory('recent')" />
          <ResourceCarousel
            v-for="subcategory in getDistinctSubcategories"
            :key="subcategory"
            :class="$style.resourceCarousel"
            :category="subcategory"
            :resources="getResourcesFromSubcategory(subcategory)"
            @openGridView="changeSelectedSubcategory(subcategory)" />
        </div>

        <div v-if="gridView" :class="$style.gridView">
          <div :class="$style.breadcrumbs">
            <MottoIcon
              :class="$style.backButton"
              name="Arrow"
              icon-color="gray"
              class="cursor-pointer"
              @click.native="activateCarouselView" />
            <p class="p2 color-darkest-gray cursor-pointer" @click="activateAllResourcesView">RESOURCES</p>
            <BaseIcon class="color-gray" name="chevron-right" />
            <p v-if="gridViewCategoryName" class="p2 color-darkest-gray cursor-pointer" @click="activateCarouselView">
              {{ gridViewCategoryName.toUpperCase() }}
            </p>
            <BaseIcon v-if="gridViewCategoryName" class="color-gray" name="chevron-right" />
            <p :class="$style.selectedBreadcrumb" class="button-text color-primary-blue">
              {{ gridViewLabel.toUpperCase() }}
            </p>
          </div>
          <h2>{{ gridViewLabel }}</h2>
          <transition-group name="fade">
            <ResourceBlock
              v-for="resource in gridViewResources"
              :key="resource.resourceId"
              :class="$style.resourceBlock"
              :resource="resource" />
          </transition-group>
          <p v-if="gridViewEmptySentence && !gridViewResources.length">{{ gridViewEmptySentence }}</p>
        </div>
      </div>

      <b-modal
        id="view-resource-modal"
        :modal-class="['right', 'modal-resource']"
        hide-footer
        hide-header
        title="BootstrapVue">
        <ModalResource :toast-component="this.$bvToast" />
      </b-modal>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.viewContainer {
  @media (max-width: $lg-breakpoint) {
    width: auto;
    min-width: unset;
  }

  width: auto;
}

.headerContent {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;
  }

  display: flex;
  flex-direction: column-reverse;
}

.infoBlock {
  @media (max-width: $lg-breakpoint) {
    margin-bottom: 24px;
  }
}

.categorySelector {
  @media (max-width: $lg-breakpoint) {
    margin-bottom: 0;
  }

  margin-bottom: 24px;
}

.resourceBlock {
  @media (max-width: $lg-breakpoint) {
    width: auto;
  }

  min-width: 275px;
}

.resourceCarousel {
  margin-top: 40px;
}

.gridView {
  > span {
    @media (max-width: $lg-breakpoint) {
      grid-template-columns: repeat(auto-fill, minmax(327px, 1fr));
      grid-gap: 16px 16px;
    }

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
    grid-gap: 32px 24px;
    margin-top: 24px;
  }
}

.breadcrumbs {
  display: flex;
  align-items: center;
  margin-bottom: 42px;

  > svg {
    margin: -2px 13px 0;
  }
}

.backButton {
  margin-right: 29px;

  > svg {
    width: 30px;
    height: 30px;
  }
}

.selectedBreadcrumb {
  &::after {
    float: left;
    width: 100%;
    height: 4px;
    content: '';
    background: $primary-blue;
    border-radius: 5px;
  }
}

.title {
  @media (min-width: $lg-breakpoint) {
    color: $primary-blue;
  }
}

.emptySentence {
  color: $dark-gray;
  text-align: center;
}
</style>
